import { useCallback, useEffect, useState } from 'react'
import { ShieldAlert, Plus } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchRequests,
  requestAccess,
  approveRequest,
  denyRequest,
  revokeRequest,
  fetchBreakGlass,
  activateBreakGlass,
  SCOPES,
  JIT_STR as S,
  REQ_STATUS_LABEL,
  type AccessRequest,
  type BreakGlassEvent,
} from './jitApi'

const statusPill: Record<string, string> = {
  pending: 'bg-warning/10 text-warning',
  approved: 'bg-success/10 text-success',
  denied: 'bg-surface-3 text-tertiary',
  revoked: 'bg-danger/10 text-danger',
  expired: 'bg-surface-3 text-tertiary',
}

function useLoad<T>(fn: (s: AbortSignal) => Promise<T>, dep: unknown) {
  const [data, setData] = useState<T | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fn(ac.signal)
      .then(setData)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, dep])
  return { data, err, reload: useCallback(() => setTick((t) => t + 1), []) }
}

function ScopeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <>
      <input
        list="jit-scopes"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={S.scope}
        className="rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
      />
      <datalist id="jit-scopes">
        {SCOPES.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </>
  )
}

/* ── A18.07 — JIT huquq so'rovlari ─────────────────────────────── */
export function JITAccessScreen() {
  const { toast } = useToast()
  const [status, setStatus] = useState('')
  const { data, err, reload } = useLoad<AccessRequest[]>((s) => fetchRequests(status, s), status)
  const [scope, setScope] = useState('')
  const [reason, setReason] = useState('')
  const [dur, setDur] = useState(60)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!scope.trim()) return
    setBusy(true)
    try {
      await requestAccess(scope.trim(), reason.trim())
      setReason('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn()
      toast(S.done, { tone: 'success' })
      reload()
    } catch (e) {
      toast(e instanceof ApiError && e.isForbidden ? S.fourEyes : S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader title={S.title} screenId="A18.07" description={S.desc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* Request form */}
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border-default bg-surface-2 p-3">
            <ScopeInput value={scope} onChange={setScope} />
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={S.reason}
              className="min-w-[160px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <button type="button" onClick={submit} disabled={busy || !scope.trim()} className="flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50">
              <Plus size={14} />
              {S.request}
            </button>
          </div>

          {/* Filter + grant duration */}
          <div className="flex items-center justify-between text-sm">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-secondary">
              <option value="">{S.all}</option>
              <option value="pending">{S.pending}</option>
              <option value="approved">{S.approved}</option>
              <option value="denied">{S.denied}</option>
              <option value="revoked">{S.revoked}</option>
            </select>
            <label className="flex items-center gap-1.5 text-xs text-tertiary">
              {S.duration}:
              <select value={dur} onChange={(e) => setDur(Number(e.target.value))} className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-secondary">
                <option value={60}>60</option>
                <option value={240}>240</option>
                <option value={480}>480</option>
                <option value={1440}>1440</option>
              </select>
            </label>
          </div>

          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !data ? (
            <div className="space-y-1.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : data.length === 0 ? (
            <EmptyState title={S.empty} />
          ) : (
            <div className="space-y-2">
              {data.map((r) => (
                <div key={r.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-primary">{r.scope}</span>
                        <span className={`rounded px-1.5 py-0.5 text-xs ${statusPill[r.status] ?? ''}`}>
                          {REQ_STATUS_LABEL[r.status] ?? r.status}
                        </span>
                        {r.active && <span className="text-xs text-success">● {S.active}</span>}
                      </div>
                      {r.reason && <p className="mt-1 text-sm text-secondary">{r.reason}</p>}
                      <p className="mt-0.5 text-xs text-tertiary">
                        {S.requester}: {r.requester || '—'}
                        {r.approver && ` · ${S.approver}: ${r.approver}`}
                        {r.expires_at && ` · ${S.expires}: ${new Date(r.expires_at).toLocaleString()}`}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      {r.status === 'pending' && (
                        <>
                          <button type="button" onClick={() => act(() => approveRequest(r.id, dur))} className="rounded-md bg-success px-2.5 py-1 text-xs text-white">
                            {S.approve}
                          </button>
                          <button type="button" onClick={() => act(() => denyRequest(r.id))} className="rounded-md border border-border-default px-2.5 py-1 text-xs text-secondary hover:text-primary">
                            {S.deny}
                          </button>
                        </>
                      )}
                      {r.status === 'approved' && r.active && (
                        <button type="button" onClick={() => act(() => revokeRequest(r.id))} className="rounded-md bg-danger px-2.5 py-1 text-xs text-white">
                          {S.revoke}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/* ── A18.08 — Break-glass jurnali ──────────────────────────────── */
export function BreakGlassScreen() {
  const { toast } = useToast()
  const { data, err, reload } = useLoad<BreakGlassEvent[]>((s) => fetchBreakGlass(s), null)
  const [scope, setScope] = useState('')
  const [reason, setReason] = useState('')
  const [dur, setDur] = useState(60)
  const [busy, setBusy] = useState(false)

  const activate = async () => {
    if (!scope.trim() || !reason.trim()) {
      toast(S.failed, { tone: 'error' })
      return
    }
    if (!window.confirm(`Break-glass: ${scope} (${dur}m)?`)) return
    setBusy(true)
    try {
      await activateBreakGlass(scope.trim(), reason.trim(), dur)
      setReason('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.bgTitle} screenId="A18.08" description={S.bgDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* Activate (emergency) */}
          <div className="rounded-lg border border-danger/40 bg-danger/5 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-danger">
              <ShieldAlert size={15} />
              {S.bgTitle}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ScopeInput value={scope} onChange={setScope} />
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={`${S.reason} *`}
                className="min-w-[160px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
              />
              <select value={dur} onChange={(e) => setDur(Number(e.target.value))} className="rounded-md border border-border-default bg-surface-1 px-2 py-2 text-sm text-secondary">
                <option value={30}>30m</option>
                <option value={60}>60m</option>
                <option value={240}>240m</option>
              </select>
              <button type="button" onClick={activate} disabled={busy || !scope.trim() || !reason.trim()} className="rounded-md bg-danger px-3 py-2 text-sm text-white disabled:opacity-50">
                {S.activate}
              </button>
            </div>
          </div>

          {/* Log */}
          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !data ? (
            <div className="space-y-1.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : data.length === 0 ? (
            <EmptyState title={S.emptyBg} />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-default">
              {data.map((e, i) => (
                <div key={e.id} className={`px-4 py-2.5 ${i > 0 ? 'border-t border-border-subtle' : ''}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-primary">{e.scope}</span>
                    {e.active ? (
                      <span className="text-xs text-danger">● {S.active}</span>
                    ) : (
                      <span className="text-xs text-tertiary">{e.revoked_at ? S.revoked : S.expired}</span>
                    )}
                    <span className="ml-auto text-xs text-tertiary">{new Date(e.created_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-0.5 text-sm text-secondary">{e.reason}</p>
                  <p className="text-xs text-tertiary">{S.admin}: {e.admin || '—'} · {S.expires}: {new Date(e.expires_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
